/**
 * Apple Pay / Stripe Express Checkout（ヤギプライム課金フォーム共用）。
 */
import axios from 'axios'
import { getCurrentInstance, nextTick, ref, type Ref } from 'vue'
import { loadStripe } from '@stripe/stripe-js'
import VueCookies from 'vue-cookies'
import { useAuth } from '@/composables/useAuth'
import { isPrivateRelayEmail } from '@/utils/payment/isPrivateRelayEmail'
import { emitter } from '@/plugins/emitter'

const SHOW_PRIVATE_RELAY_WARNING = 'show-private-relay-warning'

type HostVm = {
  $varuna: { endPoints: Record<string, string> }
  $message: (opts: Record<string, unknown>) => void
}

type ApplePayOpts = {
  publishableKey: string
  price: Ref<number>
  serviceName: Ref<string>
  isSubscription: Ref<boolean>
  notificationSetting: Ref<string>
}

export function useApplePayCheckout(opts: ApplePayOpts) {
  const vm = getCurrentInstance()!.proxy as unknown as HostVm
  const { user, getClient } = useAuth()

  const isApplePayAvailable = ref(false)
  const isDisplayApplePayButton = ref(false)
  const paymentRequest = ref<any>(null) as Ref<any>
  let expressCheckoutElement: any = null
  let clientSecret = ''

  function currentUid(): string {
    return getClient()?.user?.uid ?? user.value?.uid ?? ''
  }

  function currentEmail(): string | undefined {
    return getClient()?.user?.email ?? user.value?.email ?? undefined
  }

  async function initializeExpressCheckout() {
    const stripe = await loadStripe(opts.publishableKey)
    if (!stripe) return
    const AMOUNT = opts.price.value
    const CURRENCY = 'jpy'
    const LABEL = opts.serviceName.value

    if (!paymentRequest.value) {
      paymentRequest.value = stripe.paymentRequest({
        country: 'JP',
        currency: CURRENCY,
        total: {
          label: LABEL,
          amount: AMOUNT
        },
        requestPayerName: true,
        requestPayerEmail: true
      })
      const result = await paymentRequest.value.canMakePayment()
      isApplePayAvailable.value = !!(result && result.applePay)
    }
  }

  async function applePayMount() {
    if (isPrivateRelayEmail(currentEmail())) {
      emitter.emit(SHOW_PRIVATE_RELAY_WARNING, () => {
        executeApplePay().catch(() => {})
      })
      return
    }
    await executeApplePay()
  }

  async function executeApplePay() {
    const stripe = await loadStripe(opts.publishableKey)
    if (!stripe) return
    const AMOUNT = opts.price.value
    const LABEL = opts.serviceName.value
    const CURRENCY = 'jpy'
    const mode = opts.isSubscription.value ? 'subscription' : 'payment'
    const elements = stripe.elements({
      mode,
      amount: AMOUNT,
      currency: CURRENCY
    })

    const customerForm = new FormData()
    customerForm.append('uuid', currentUid())
    customerForm.append('subscription_type', '1')
    customerForm.append('notification_setting', opts.notificationSetting.value)
    const routeId = (VueCookies as any).get('_YAGISH_YAGIPRIME_ROUTE_ID')
    customerForm.append('route', routeId || '')

    if (opts.isSubscription.value) {
      const response = await axios.post(
        vm.$varuna.endPoints.createStripeCustomer,
        customerForm
      )
      clientSecret = response.data.client_secret
    }

    isDisplayApplePayButton.value = true
    const applePayButton = opts.isSubscription.value ? 'subscribe' : 'buy'
    expressCheckoutElement = (elements as any).create('expressCheckout', {
      paymentRequest: paymentRequest.value,
      buttonType: {
        applePay: applePayButton
      }
    })

    await nextTick()
    expressCheckoutElement.mount('#payment-request-button')

    expressCheckoutElement.on('click', async (e: any) => {
      const options = opts.isSubscription.value
        ? {
            clientSecret,
            applePay: {
              recurringPaymentRequest: {
                paymentDescription: LABEL,
                regularBilling: {
                  amount: AMOUNT,
                  label: LABEL,
                  recurringPaymentIntervalUnit: 'day',
                  recurringPaymentIntervalCount: 30
                },
                billingAgreement:
                  '30日間ごとに550円(税込)となります。\n本サービスにお申込みいただき、お客様の登録が完了した時点でご利用代金が引き落とされると同時に、サービスが開始されます。\n初回のサービス提供期間終了後、自動更新され、お客様の選択により、以降30日間ごとに550円（税込）がかかります。\n利用代金は更新されたサービス期間の毎初日に登録された決済情報から引き落とされます。\nお申込み手続きの完了後に、代金の返金やキャンセルはできません。※解約はマイページの「アカウント情報」からお申込みいただけます。',
                managementURL: 'https://rirekisho.yagish.jp/mypage/account'
              }
            }
          }
        : {}
      e.resolve(options)
    })

    expressCheckoutElement.on('confirm', async (event: any) => {
      try {
        if (opts.isSubscription.value) {
          const { error: submitError } = await elements.submit()
          if (submitError) {
            vm.$message({
              message: submitError.message,
              type: 'error'
            })
            event.complete('fail')
            return
          }
          const { error: confirmError } = await stripe.confirmPayment({
            elements,
            clientSecret,
            confirmParams: {
              return_url: window.location.href
            }
          })

          if (confirmError) {
            vm.$message({
              message: confirmError.message || '決済に失敗しました。',
              type: 'error'
            })
            event.complete('fail')
          } else {
            event.complete('success')
          }
        } else {
          event.complete('success')
        }
      } catch (error) {
        console.error('支払いエラー:', error)
        vm.$message({
          message: '支払いに失敗しました。',
          type: 'error'
        })
        event.complete('fail')
      }
    })
  }

  function hideApplePayButton() {
    isDisplayApplePayButton.value = false
  }

  return {
    isApplePayAvailable,
    isDisplayApplePayButton,
    initializeExpressCheckout,
    applePayMount,
    hideApplePayButton
  }
}
